import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { CATEGORIES as DEFAULT_CATEGORIES, CANDIDATE_STATUSES, countryChip, formatDate, statusBadgeColor } from "../utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import DetailPanel from "../components/DetailPanel";
import Layout from "../components/Layout";
import { Search, Upload, Plus, FileUp, CheckCircle2, XCircle, Package, Inbox, MessageCircle, ListPlus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTrackedSearch, trackView } from "../activityTracker";

const POLL_MS = 10000;

// Normalise a Malaysian/foreign phone to a wa.me-safe digits string.
// Assumes default country = Malaysia (60) when the number starts with 0 and has no +.
function waDigits(raw) {
  if (!raw) return "";
  let s = String(raw).trim();
  const hasPlus = s.startsWith("+");
  s = s.replace(/\D/g, "");
  if (hasPlus) return s;
  if (s.startsWith("0")) return "60" + s.slice(1); // MY local -> international
  if (s.startsWith("60")) return s;
  return s;
}

function openWhatsAppJobOffer(candidate, senderName) {
  const digits = waDigits(candidate.phone);
  if (!digits) return;
  const firstName = (candidate.name || "").split(/\s+/)[0] || "";
  const hello = firstName ? `Hello ${firstName}` : "Hello";
  const text =
    `${hello}, this is ${senderName || "Mercy Consultancy"} from Mercy Consultancy. ` +
    `I would like to follow up regarding your job application` +
    (candidate.position ? ` for ${candidate.position}` : "") +
    `.\n\nAre you still interested in this opportunity? If yes, please let me know a ` +
    `good time to call you.\n\nThank you.`;
  const url = `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener");
}

export default function Candidates() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Live categories fetched from backend so admin adds show up everywhere.
  const [CATEGORIES, setCategories] = useState(DEFAULT_CATEGORIES);
  const [catCounts, setCatCounts] = useState({}); // name -> count, for manage dialog
  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data.map((c) => c.name));
      setCatCounts(Object.fromEntries(data.map((c) => [c.name, c.count])));
    } catch { /* keep defaults */ }
  }, []);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const visibleCats = useMemo(() => {
    if (user?.role === "admin") return CATEGORIES;
    const tabs = user?.candidate_tabs_allowed || [];
    if (tabs.includes("*")) return CATEGORIES;
    return CATEGORIES.filter((c) => tabs.includes(c));
  }, [user, CATEGORIES]);

  const [category, setCategory] = useState(visibleCats[0] || CATEGORIES[0]);
  const [manageCatsOpen, setManageCatsOpen] = useState(false);
  // Keep active tab valid when categories change (e.g., after deletion)
  useEffect(() => {
    if (visibleCats.length > 0 && !visibleCats.includes(category)) {
      setCategory(visibleCats[0]);
    }
  }, [visibleCats, category]);
  const [counts, setCounts] = useState({});
  const [rows, setRows] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [q, setQ] = useState("");
  // Log searches (debounced) and detail-views into the activity audit trail.
  useTrackedSearch("candidates", q);
  const [country, setCountry] = useState("all");
  const [permit, setPermit] = useState("all");
  const [status, setStatus] = useState("all");
  const [staffFilter, setStaffFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [batchReqOpen, setBatchReqOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [myPendingCats, setMyPendingCats] = useState([]);
  const [progress, setProgress] = useState([]);

  const load = useCallback(async () => {
    const params = { category };
    if (q) params.q = q;
    if (country !== "all") params.country = country;
    if (permit !== "all") params.permit = permit;
    if (status !== "all") params.status = status;
    if (staffFilter !== "all") params.staff = staffFilter;
    try {
      const calls = [
        api.get("/candidates", { params }),
        api.get("/candidates/counts"),
        api.get("/staff"),
      ];
      if (user?.role !== "admin") {
        calls.push(api.get("/batch-requests"));
        calls.push(api.get("/candidates/progress"));
      }
      const results = await Promise.all(calls);
      setRows(results[0].data);
      setCounts(results[1].data);
      setStaffList(results[2].data);
      if (user?.role !== "admin") {
        const pending = (results[3]?.data || [])
          .filter((r) => r.status === "Pending")
          .map((r) => r.category);
        setMyPendingCats(pending);
        setProgress(results[4]?.data || []);
      }
    } catch (e) { /* silent */ }
  }, [category, q, country, permit, status, staffFilter, user?.role]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const uniqueCountries = useMemo(
    () => Array.from(new Set(rows.map((r) => r.country).filter(Boolean))),
    [rows]
  );

  const currentProgress = useMemo(
    () => progress.find((p) => p.category === category) || null,
    [progress, category]
  );
  const pctWorked = currentProgress && currentProgress.quota > 0
    ? Math.min(100, Math.round((currentProgress.worked / currentProgress.quota) * 100))
    : 0;

  const claim = async (c) => {
    try { await api.post(`/candidates/${c.id}/claim`); toast.success(`Claimed ${c.name}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to claim"); }
  };
  const release = async (c) => {
    try { await api.post(`/candidates/${c.id}/release`); toast.success(`Released ${c.name}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const changeStatus = async (c, newStatus) => {
    try { await api.post(`/candidates/${c.id}/status`, { status: newStatus }); load(); }
    catch (e) { toast.error("Failed to update status"); }
  };

  const openDetail = (c) => {
    setSelected(c); setOpen(true);
    try { trackView("candidate", c.id, c.name, "candidates"); } catch { /* noop */ }
  };

  return (
    <Layout title="Candidates">
    <div className="space-y-4" data-testid="candidates-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold tracking-tight">Candidates</h1>
        <div className="flex items-center gap-2">
          {user?.role !== "admin" && (
            myPendingCats.includes(category) ? (
              <span
                data-testid="batch-pending-indicator"
                className="inline-flex items-center gap-1.5 text-xs num px-3 py-1.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/30"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
                Request submitted — waiting admin approval
              </span>
            ) : (
              <Button variant="outline" onClick={() => setBatchReqOpen(true)}
                className="border-slate-700 hover:border-blue-500" data-testid="request-batch-btn">
                <Package className="w-4 h-4 mr-1.5" /> Request Next Batch
              </Button>
            )
          )}
          {user?.role === "admin" && (
            <>
              <Button variant="outline" onClick={() => setImportOpen(true)}
                className="border-slate-700 hover:border-blue-500" data-testid="import-candidates-btn">
                <FileUp className="w-4 h-4 mr-1.5" /> Import CSV
              </Button>
              <Button variant="outline" onClick={() => setManageCatsOpen(true)}
                className="border-slate-700 hover:border-blue-500" data-testid="manage-categories-btn">
                <ListPlus className="w-4 h-4 mr-1.5" /> Manage categories
              </Button>
            </>
          )}
          <Button onClick={() => setAddOpen(true)}
            className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="add-candidate-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add
          </Button>
        </div>
      </div>

      {/* Category tabs */}
      <Tabs value={category} onValueChange={setCategory}>
        <div className="overflow-x-auto scrollbar-thin">
          <TabsList className="bg-transparent p-0 h-auto border-b hairline rounded-none w-full justify-start">
            {visibleCats.map((c) => (
              <TabsTrigger
                key={c} value={c}
                data-testid={`cat-tab-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-blue-500 text-slate-400 px-3 py-2 whitespace-nowrap"
              >
                <span>{c}</span>
                <span className="ml-2 text-xs num text-slate-500">({counts[c] ?? 0})</span>
                {myPendingCats.includes(c) && (
                  <span
                    className="ml-1.5 w-1.5 h-1.5 rounded-full bg-amber-400"
                    title="Request pending admin approval"
                  />
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
      </Tabs>

      {/* Phase 2: staff progress bar for the active category */}
      {user?.role !== "admin" && currentProgress && (
        <div
          data-testid="quota-progress"
          className="rounded-md border hairline surface-1 px-4 py-3 flex items-center gap-4 flex-wrap"
        >
          <div className="flex items-baseline gap-1.5 min-w-[150px]">
            <span className="text-lg font-semibold num tabular-nums">{currentProgress.worked}</span>
            <span className="text-sm text-slate-400">of</span>
            <span className="text-lg font-semibold num tabular-nums">{currentProgress.quota}</span>
            <span className="text-xs text-slate-500 ml-1">worked</span>
          </div>
          <div className="flex-1 min-w-[200px] h-1.5 bg-slate-800 rounded overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pctWorked}%` }} />
          </div>
          <div className="text-xs text-slate-500 num">
            {currentProgress.quota} of {currentProgress.total} in category
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="relative col-span-2 md:col-span-1">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, email, city"
            className="pl-8 bg-[#0f1626] border-slate-800" data-testid="search-input" />
        </div>
        <Select value={country} onValueChange={setCountry}>
          <SelectTrigger className="bg-[#0f1626] border-slate-800" data-testid="filter-country"><SelectValue placeholder="All Countries" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            {uniqueCountries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={permit} onValueChange={setPermit}>
          <SelectTrigger className="bg-[#0f1626] border-slate-800" data-testid="filter-permit"><SelectValue placeholder="All Permits" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Permits</SelectItem>
            <SelectItem value="yes">Has Permit</SelectItem>
            <SelectItem value="no">No Permit</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="bg-[#0f1626] border-slate-800" data-testid="filter-status"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {CANDIDATE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={staffFilter} onValueChange={setStaffFilter}>
          <SelectTrigger className="bg-[#0f1626] border-slate-800" data-testid="filter-staff"><SelectValue placeholder="All Staff" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Staff</SelectItem>
            <SelectItem value="unclaimed">Unclaimed</SelectItem>
            {staffList.map((s) => <SelectItem key={s.id} value={s.id}>{s.display_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block rounded-lg border hairline surface-1 overflow-hidden">
        <table className="w-full text-sm" data-testid="candidates-table">
          <thead>
            <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b hairline">
              <th className="w-6"></th>
              <th className="py-2.5 px-3">Name</th>
              <th className="px-3">Phone</th>
              <th className="px-3">Email</th>
              <th className="px-3">Position</th>
              <th className="px-3">City</th>
              <th className="px-3">Country</th>
              <th className="px-3 text-center">Permit</th>
              <th className="px-3 num">Added</th>
              <th className="px-3">Handler</th>
              <th className="px-3">Status</th>
              <th className="px-3 text-right">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="row-hover border-b border-slate-900/80 cursor-pointer"
                onClick={() => openDetail(c)} data-testid={`candidate-row-${c.id}`}>
                <td className="pl-3"><span className="inline-block w-2 h-2 rounded-full"
                  style={{ background: c.claimed_by_color || "transparent",
                    border: c.claimed_by_color ? "none" : "1px solid #334155" }} /></td>
                <td className="py-2.5 px-3 font-medium">{c.name}</td>
                <td className="px-3 num text-slate-300">
                  {c.phone ? (
                    <span className="flex items-center gap-1.5">
                      <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()}
                        className="hover:text-blue-400">{c.phone}</a>
                      <button type="button"
                        onClick={(e) => { e.stopPropagation(); openWhatsAppJobOffer(c, user?.display_name); }}
                        title="WhatsApp — job application follow-up"
                        className="text-emerald-400 hover:text-emerald-300"
                        data-testid={`candidate-wa-${c.id}`}>
                        <MessageCircle className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ) : "—"}
                </td>
                <td className="px-3 text-slate-300">
                  {c.email ? <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()}
                    className="hover:text-blue-400">{c.email}</a> : "—"}
                </td>
                <td className="px-3 text-slate-300">{c.position || "—"}</td>
                <td className="px-3 text-slate-300">{c.city || "—"}</td>
                <td className="px-3"><span className="text-xs px-1.5 py-0.5 rounded border hairline surface-2 num">{countryChip(c.country)}</span></td>
                <td className="px-3 text-center">
                  {c.permit === "yes" ? <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
                    : c.permit === "no" ? <XCircle className="w-4 h-4 text-red-400 inline" />
                    : <span className="text-slate-600">—</span>}
                </td>
                <td className="px-3 num text-slate-400">{formatDate(c.date_added)}</td>
                <td className="px-3" onClick={(e) => e.stopPropagation()}>
                  {c.claimed_by ? (
                    <span className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border hairline surface-2">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.claimed_by_color }} />
                      {c.claimed_by_name}
                      {(user?.role === "admin" || c.claimed_by === user?.id) && (
                        <button onClick={() => release(c)} className="ml-1 text-slate-500 hover:text-white">×</button>
                      )}
                    </span>
                  ) : (
                    <Button size="sm" variant="outline"
                      className="h-7 text-xs border-slate-700 hover:border-blue-500"
                      onClick={() => claim(c)} data-testid={`claim-btn-${c.id}`}>+ Claim</Button>
                  )}
                </td>
                <td className="px-3" onClick={(e) => e.stopPropagation()}>
                  <Select value={c.status} onValueChange={(v) => changeStatus(c, v)}>
                    <SelectTrigger className="h-7 text-xs bg-[#0f1626] border-slate-800 min-w-[120px]">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusBadgeColor(c.status) }} />
                        <span>{c.status}</span>
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {CANDIDATE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 text-right num text-slate-400">{c.notes_count || 0}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={12} className="py-14 text-center text-slate-500">
                <Inbox className="w-6 h-6 mx-auto mb-2 opacity-50" />
                No candidates match your filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {rows.map((c) => (
          <div key={c.id} className="rounded-lg border hairline surface-1 p-3"
            onClick={() => openDetail(c)} data-testid={`candidate-card-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {c.claimed_by_color && <span className="w-2 h-2 rounded-full" style={{ background: c.claimed_by_color }} />}
                  <div className="font-medium truncate">{c.name}</div>
                </div>
                <div className="text-xs text-slate-400 num mt-0.5">{c.phone || "—"} · {countryChip(c.country)}</div>
                <div className="text-xs text-slate-500 mt-0.5">{c.position || "—"}</div>
              </div>
              <span className="text-xs num px-2 py-0.5 rounded-full border hairline surface-2"
                style={{ color: statusBadgeColor(c.status) }}>{c.status}</span>
            </div>
            <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {!c.claimed_by ? (
                <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-500" onClick={() => claim(c)}>Claim</Button>
              ) : c.claimed_by === user?.id ? (
                <Button size="sm" variant="outline" className="h-7 text-xs border-slate-700" onClick={() => release(c)}>Release</Button>
              ) : (
                <span className="text-xs text-slate-400">Claimed by {c.claimed_by_name}</span>
              )}
              {c.phone && (
                <a href={`tel:${c.phone}`} className="ml-auto text-xs text-blue-400"
                   onClick={(e) => e.stopPropagation()}>Call</a>
              )}
              {c.phone && (
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); openWhatsAppJobOffer(c, user?.display_name); }}
                  className="text-xs text-emerald-400 flex items-center gap-1"
                  data-testid={`candidate-wa-m-${c.id}`}>
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <DetailPanel
        open={open}
        onClose={() => setOpen(false)}
        entityType="candidate"
        entity={selected}
        onChanged={load}
      />

      <AddCandidateDialog open={addOpen} onOpenChange={setAddOpen} onCreated={load}
        defaultCategory={category} categories={visibleCats} />
      <BatchRequestDialog open={batchReqOpen} onOpenChange={setBatchReqOpen}
        defaultCategory={category} categories={CATEGORIES} />
      <ImportCsvDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
      <ManageCategoriesDialog open={manageCatsOpen} onOpenChange={setManageCatsOpen}
        onChanged={() => { loadCategories(); load(); }} />
    </div>
    </Layout>
  );
}

function AddCandidateDialog({ open, onOpenChange, onCreated, defaultCategory, categories }) {
  const cats = categories || DEFAULT_CATEGORIES;
  const [form, setForm] = useState({
    name: "", phone: "", email: "", position: "", city: "", country: "",
    permit: "", category: defaultCategory, status: "New",
  });
  useEffect(() => { setForm((f) => ({ ...f, category: defaultCategory })); }, [defaultCategory]);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

  const submit = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      await api.post("/candidates", form);
      toast.success("Candidate added");
      onCreated?.(); onOpenChange(false);
      setForm({ name: "", phone: "", email: "", position: "", city: "", country: "", permit: "", category: defaultCategory, status: "New" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg" data-testid="add-candidate-dialog">
        <DialogHeader><DialogTitle>Add candidate</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field2 label="Full name" value={form.name} onChange={set("name")} required />
          <Field2 label="Phone" value={form.phone} onChange={set("phone")} />
          <Field2 label="Email" value={form.email} onChange={set("email")} />
          <Field2 label="Position" value={form.position} onChange={set("position")} />
          <Field2 label="City" value={form.city} onChange={set("city")} />
          <Field2 label="Country" value={form.country} onChange={set("country")} />
          <div>
            <Label className="text-xs text-slate-400">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Permit</Label>
            <Select value={form.permit || "none"} onValueChange={(v) => setForm({ ...form, permit: v === "none" ? "" : v })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-500" data-testid="add-candidate-submit">Add candidate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field2({ label, value, onChange, required }) {
  return (
    <div>
      <Label className="text-xs text-slate-400">{label}{required && <span className="text-red-400"> *</span>}</Label>
      <Input value={value} onChange={onChange} className="bg-[#0a0e1a] border-slate-800 mt-1" />
    </div>
  );
}

function BatchRequestDialog({ open, onOpenChange, defaultCategory, categories }) {
  const cats = categories || DEFAULT_CATEGORIES;
  const [cat, setCat] = useState(defaultCategory);
  const [qty, setQty] = useState(30);
  const [reason, setReason] = useState("");
  useEffect(() => setCat(defaultCategory), [defaultCategory]);

  const submit = async () => {
    try {
      await api.post("/batch-requests", { category: cat, quantity_requested: Number(qty), reason });
      toast.success("Batch request sent. Admin will review shortly.");
      onOpenChange(false);
      setReason("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md" data-testid="batch-request-dialog">
        <DialogHeader><DialogTitle>Request next batch</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-400">Category</Label>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Quantity</Label>
            <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="bg-[#0a0e1a] border-slate-800 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Reason (optional)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="bg-[#0a0e1a] border-slate-800 mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-500" data-testid="batch-request-submit">Send request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportCsvDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [mode, setMode] = useState("preview");  // preview | merge | skip
  const [defaultCategory, setDefaultCategory] = useState("");
  const [defaultCountry, setDefaultCountry] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || categories.length) return;
    api.get("/categories").then(({ data }) =>
      setCategories((data || []).map((c) => c.name))
    ).catch(() => {});
  }, [open, categories.length]);

  const submit = async (runMode) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setLoading(true);
    try {
      const params = new URLSearchParams({ mode: runMode });
      if (defaultCategory) params.set("default_category", defaultCategory);
      if (defaultCountry) params.set("default_country", defaultCountry);
      const { data } = await api.post(`/candidates/import?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport({ ...data, ranMode: runMode });
      if (runMode === "preview") {
        toast.success(
          `Preview: ${data.summary.new} new, ${data.summary.duplicate} duplicates, ${data.summary.invalid} invalid`,
          { duration: 6000 },
        );
      } else {
        toast.success(`Imported ${data.summary.imported}, updated ${data.summary.updated}, skipped ${data.summary.skipped}`);
        onDone?.();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setLoading(false); }
  };

  const reset = () => { setReport(null); setFile(null); setMode("preview"); };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-3xl max-h-[85vh] flex flex-col" data-testid="import-candidates-dialog">
        <DialogHeader><DialogTitle>Import candidates (CSV / Excel)</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          <p className="text-xs text-slate-400">
            Accepts <span className="num">.csv .xlsx .xls</span>. Column headers are auto-mapped — common Malay/English variations like
            <span className="num"> NAMA, NAMA, LOKASI, NEGARA, JAWATAN, NO TELEFON, WHATSAPP</span> are recognised. Rows with name = "<span className="num">-</span>" or empty are skipped.
            Duplicates are detected by phone (digits-only), name+email, and within the file itself.
          </p>
          <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); }}
            className="bg-[#0a0e1a] border-slate-800" data-testid="import-file-input" />

          {/* Default category + country pickers */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Default category (for rows without one)</Label>
              <Select value={defaultCategory || "__none"}
                onValueChange={(v) => setDefaultCategory(v === "__none" ? "" : v)}>
                <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="import-default-category">
                  <SelectValue placeholder="None — file must have category column" />
                </SelectTrigger>
                <SelectContent className="max-h-[260px]">
                  <SelectItem value="__none">— none —</SelectItem>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 num">Default country</Label>
              <Input value={defaultCountry} onChange={(e) => setDefaultCountry(e.target.value)}
                className="bg-[#0a0e1a] border-slate-800 mt-1"
                placeholder="e.g. malaysia, bangladesh"
                data-testid="import-default-country" />
            </div>
          </div>

          {file && !report && (
            <div className="text-xs text-slate-400">
              📁 {file.name} · click <b>Preview</b> first to see exactly what will happen.
            </div>
          )}

          {report && (
            <div className="space-y-3">
              {/* Detected column mapping — always shown, helps debug header issues */}
              {report.header_map && report.header_map.length > 0 && (
                <div className="rounded-md border hairline p-2.5 surface-2" data-testid="import-header-map">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 num mb-1.5">Detected columns</div>
                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                    {report.header_map.map((h, i) => (
                      <span key={i} className={
                        "px-2 py-0.5 rounded num " +
                        (h.mapped_to
                          ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200"
                          : "bg-slate-700/40 border border-slate-700 text-slate-400")
                      }>
                        <b>{h.original || "—"}</b>
                        <span className="opacity-60"> → {h.mapped_to || "ignored"}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Big actionable banner when invalid rows exist — explains how to fix */}
              {report.summary.invalid > 0 && (() => {
                const invalidRows = report.rows.filter((r) => r.outcome === "invalid");
                const reasonCounts = invalidRows.reduce((acc, r) => {
                  const reason = r.reason || "unknown";
                  acc[reason] = (acc[reason] || 0) + 1;
                  return acc;
                }, {});
                const noCategoryCount = Object.entries(reasonCounts)
                  .filter(([k]) => k.startsWith("no category"))
                  .reduce((sum, [, v]) => sum + v, 0);
                const missingNameCount = reasonCounts["missing name"] || 0;
                return (
                  <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-amber-100" data-testid="import-invalid-banner">
                    <div className="text-sm font-semibold">⚠ {report.summary.invalid} of {report.summary.total_rows} rows can't be imported.</div>
                    <div className="text-xs mt-2 space-y-1.5">
                      {noCategoryCount > 0 && (
                        <div>
                          • <b>{noCategoryCount} rows have no category.</b> Pick a value in the
                          <span className="px-1.5 py-0.5 mx-1 rounded bg-amber-400/20 border border-amber-400/40 num">Default category</span>
                          dropdown above (e.g. <span className="num">Loading / Unloading</span> for CEVA), then click <b>Preview</b> again.
                        </div>
                      )}
                      {missingNameCount > 0 && (
                        <div>
                          • <b>{missingNameCount} rows are missing a name.</b> Make sure your file has a <span className="num">NAMA</span> / <span className="num">Name</span> column with a value on every row.
                        </div>
                      )}
                      {Object.entries(reasonCounts)
                        .filter(([k]) => !k.startsWith("no category") && k !== "missing name")
                        .map(([k, v]) => (
                          <div key={k}>• <b>{v} rows:</b> {k}</div>
                        ))}
                    </div>
                  </div>
                );
              })()}

              <div className="flex flex-wrap gap-2 text-xs num">
                <span className="px-2 py-1 rounded border border-slate-700 surface-2">
                  Total rows: <b>{report.summary.total_rows}</b>
                </span>
                <span className="px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200">
                  New: <b>{report.summary.new}</b>
                </span>
                <span className="px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200">
                  Duplicates: <b>{report.summary.duplicate}</b>
                </span>
                {report.summary.invalid > 0 && (
                  <span className="px-2 py-1 rounded border border-red-500/40 bg-red-500/10 text-red-200">
                    Invalid: <b>{report.summary.invalid}</b>
                  </span>
                )}
                {report.ranMode !== "preview" && (
                  <>
                    <span className="px-2 py-1 rounded border border-blue-500/40 bg-blue-500/10 text-blue-200">
                      Imported: <b>{report.summary.imported}</b>
                    </span>
                    {report.summary.updated > 0 && (
                      <span className="px-2 py-1 rounded border border-blue-500/40 bg-blue-500/10 text-blue-200">
                        Updated: <b>{report.summary.updated}</b>
                      </span>
                    )}
                  </>
                )}
              </div>

              <div className="rounded-md border hairline overflow-hidden">
                <div className="max-h-[240px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#0a0e1a] sticky top-0">
                      <tr className="text-left text-slate-500 num">
                        <th className="px-2 py-1.5">#</th>
                        <th className="px-2 py-1.5">Name</th>
                        <th className="px-2 py-1.5">Phone</th>
                        <th className="px-2 py-1.5">Outcome</th>
                        <th className="px-2 py-1.5">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.map((r) => {
                        const tone =
                          r.outcome === "new" ? "text-emerald-300"
                          : r.outcome === "duplicate" ? "text-amber-300"
                          : "text-red-300";
                        return (
                          <tr key={r.row} className="border-t hairline">
                            <td className="px-2 py-1 num text-slate-500">{r.row}</td>
                            <td className="px-2 py-1 truncate">{r.name || "—"}</td>
                            <td className="px-2 py-1 num">{r.phone || "—"}</td>
                            <td className={`px-2 py-1 num uppercase tracking-wider ${tone}`}>
                              {r.outcome}{r.action ? ` · ${r.action}` : ""}
                            </td>
                            <td className="px-2 py-1 text-slate-400">{r.reason || ""}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {report.errors?.length > 0 && (
                <div className="rounded-md border border-red-500/30 bg-red-500/5 p-2 text-[11px] text-red-200 max-h-32 overflow-y-auto">
                  {report.errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              )}

              {report.ranMode === "preview" && report.summary.duplicate > 0 && (
                <div className="rounded-md border hairline p-3 space-y-2">
                  <Label className="text-xs text-slate-400">For duplicates:</Label>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="radio" checked={mode === "skip"} onChange={() => setMode("skip")} data-testid="import-mode-skip" />
                      <div>
                        <div><b>Skip duplicates</b> (recommended)</div>
                        <div className="text-[11px] text-slate-500">Leave existing records untouched. Only insert new candidates.</div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="radio" checked={mode === "merge"} onChange={() => setMode("merge")} data-testid="import-mode-merge" />
                      <div>
                        <div><b>Merge / overwrite</b></div>
                        <div className="text-[11px] text-slate-500">Update existing rows with new values from this file.</div>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="shrink-0 flex flex-row gap-2 sm:justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          {!report && (
            <Button onClick={() => submit("preview")} disabled={!file || loading}
              className="bg-slate-700 hover:bg-slate-600" data-testid="import-preview-btn">
              <FileUp className="w-4 h-4 mr-1.5" />{loading ? "Scanning…" : "Preview"}
            </Button>
          )}
          {report?.ranMode === "preview" && (
            <>
              <Button variant="outline" onClick={reset} className="border-slate-700">Re-pick file</Button>
              <Button onClick={() => submit(mode)} disabled={loading}
                className="bg-blue-600 hover:bg-blue-500" data-testid="import-submit">
                <Upload className="w-4 h-4 mr-1.5" />
                {loading ? "Importing…" : `Import (${mode === "skip" ? "skip" : "merge"} duplicates)`}
              </Button>
            </>
          )}
          {report?.ranMode && report.ranMode !== "preview" && (
            <Button variant="outline" onClick={reset} className="border-slate-700">
              Import another file
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function ManageCategoriesDialog({ open, onOpenChange, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // {old, draft}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setRows(data);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { if (open) { load(); setEditing(null); setNewName(""); } }, [open, load]);

  const addCat = async () => {
    const name = newName.trim();
    if (!name) return;
    setLoading(true);
    try {
      await api.post("/categories", { name });
      toast.success(`Added "${name}"`);
      setNewName("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setLoading(false); }
  };

  const renameCat = async (old_name) => {
    const new_name = (editing?.draft || "").trim();
    if (!new_name || new_name === old_name) { setEditing(null); return; }
    setLoading(true);
    try {
      const { data } = await api.put("/categories", { old_name, new_name });
      toast.success(`Renamed — ${data.migrated} candidate(s) migrated`);
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rename");
    } finally { setLoading(false); }
  };

  const deleteCat = async (name, count) => {
    if (count > 0) {
      toast.error(`Cannot delete: ${count} candidate(s) still in "${name}".`);
      return;
    }
    if (!window.confirm(`Delete category "${name}"? This cannot be undone.`)) return;
    setLoading(true);
    try {
      await api.delete(`/categories`, { params: { name } });
      toast.success(`Deleted "${name}"`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md"
        data-testid="manage-categories-dialog">
        <DialogHeader><DialogTitle>Manage job categories</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500">
          Add new job-listing tabs or rename existing ones. Deleting is only allowed if the category is empty.
        </div>

        <div className="mt-3 space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
          {rows.map((r) => (
            <div key={r.name}
              className="flex items-center gap-2 px-2 py-1.5 rounded border hairline surface-2"
              data-testid={`cat-row-${r.name}`}>
              {editing?.old === r.name ? (
                <Input value={editing.draft}
                  onChange={(e) => setEditing({ ...editing, draft: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") renameCat(r.name); }}
                  className="bg-[#0a0e1a] border-slate-800 h-8 text-sm flex-1"
                  autoFocus />
              ) : (
                <div className="flex-1 text-sm truncate">{r.name}</div>
              )}
              <span className="text-[10px] num px-1.5 py-0.5 rounded-full border hairline text-slate-400">
                {r.count}
              </span>
              {editing?.old === r.name ? (
                <>
                  <Button size="sm" variant="ghost" onClick={() => renameCat(r.name)} className="text-emerald-300 h-7 px-2">Save</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="text-slate-400 h-7 px-2">Cancel</Button>
                </>
              ) : (
                <>
                  <Button size="sm" variant="ghost"
                    onClick={() => setEditing({ old: r.name, draft: r.name })}
                    className="text-slate-300 h-7 px-2" data-testid={`cat-rename-${r.name}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost"
                    onClick={() => deleteCat(r.name, r.count)}
                    className={`h-7 px-2 ${r.count > 0 ? "text-slate-600 cursor-not-allowed" : "text-red-400 hover:text-red-300"}`}
                    data-testid={`cat-delete-${r.name}`}
                    title={r.count > 0 ? "Has candidates — reassign first" : "Delete category"}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="mt-3 pt-3 border-t hairline">
          <Label className="text-xs text-slate-400">Add new category</Label>
          <div className="flex gap-2 mt-1">
            <Input value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addCat(); }}
              placeholder="e.g. Warehouse Picker"
              className="bg-[#0a0e1a] border-slate-800 h-9 flex-1"
              data-testid="new-category-input" />
            <Button onClick={addCat} disabled={!newName.trim() || loading}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="new-category-submit">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
